import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import { 
  FaArrowUp, 
  FaArrowDown, 
  FaArrowRight, 
  FaCheckCircle, 
  FaExclamationCircle 
} from "react-icons/fa"; // Added Check and Exclamation icons
import { PiCardsBold } from "react-icons/pi";
import { IoIosArrowBack } from "react-icons/io";
import { MdOutlineFileDownload } from "react-icons/md";
import html2canvas from "html2canvas";
import FileSaver from "file-saver";
import { useReactToPrint } from "react-to-print";

import Bar from "./Charts/Bar";
import AddChart from "./ChartAdd";
import ModifyCardsDrag from "./Tools/ModifyCardDrag";
import CampaignTable from "./AddTable/CampaignType";
import Switcher7 from "./Tools/Switcher";
import AddTable from "./AddTable";
import {
  apiRequestBodyBuilder,
  getCurrencySymbol,
  getLocalCurrencyStringType,
  getPriorityColumnsFromStorage,
} from "./config/tableHelper";

import { bingads, googleads } from "../assets/index";

const GOOGLE_ADS_CARD_COLS = [
  { id: "0", title: "Clicks", key: "clicks", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "1", title: "Cost", key: "costs", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "2", title: "CTR", key: "ctr", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "3", title: "Conversion", key: "conversions", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "4", title: "Avg Cost", key: "average_cost", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "5", title: "Avg. CPC", key: "average_cpc", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "6", title: "Avg Cpm", key: "average_cpm", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "7", title: "Impr.", key: "impressions", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "8", title: "Cost/Conv", key: "cost_per_conversion", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "9", title: "Interaction Rate", key: "interaction_rate", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "10", title: "Interactions", key: "interactions", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "11", title: "Budget", key: "remaining_budget", color: "text-[#75848B]", visible: false, category: "Conversions", locked: false },
  { id: "12", title: "Conv. Rate", key: "conversion_rate", color: "text-[#75848B]", visible: false, category: "Conversions", locked: false },
];

const BING_ADS_CARD_COLS = [
  { id: "0", title: "Clicks", key: "clicks", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "1", title: "Cost", key: "costs", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "2", title: "CTR", key: "ctr", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "3", title: "Conversion", key: "conversions", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "4", title: "Avg Cost", key: "average_cost", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "5", title: "Avg. CPC", key: "average_cpc", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "6", title: "Avg Cpm", key: "average_cpm", color: "text-[#75848B]", visible: false, category: "Performance", locked: false },
  { id: "7", title: "Impr.", key: "impressions", color: "text-[#75848B]", visible: true, category: "Performance", locked: false },
  { id: "8", title: "Cost/Conv", key: "cost_per_conversion", color: "text-[#75848B]", visible: true, category: "Conversions", locked: false },
  { id: "11", title: "Budget", key: "remaining_budget", color: "text-[#75848B]", visible: false, category: "Conversions", locked: false },
  { id: "12", title: "Conv. Rate", key: "conversion_rate", color: "text-[#75848B]", visible: false, category: "Conversions", locked: false },
];

const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const datePickerRef = useRef(null);
  const cardColumnsRef = useRef(null);

  const [currencySymbol, setCurrencySymbol] = useState(getCurrencySymbol());
  const [tableVisible, setTableVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCardColumns, setShowCardColumns] = useState(false);

  // New state for Budget Alert
  const [budgetAlert, setBudgetAlert] = useState({ show: false, exists: true, message: "" });

  const [metricsData, setMetricsData] = useState({});
  const [cards, setCards] = useState(
    localStorage.getItem("platform") === "google_ads"
      ? GOOGLE_ADS_CARD_COLS
      : BING_ADS_CARD_COLS,
  );

  const [columns, setColumns] = useState([
    { id: "0", title: "Campaign Type", key: "Campaign Type", visible: true, locked: true, category: "recommended", isLocked: true },
    { id: "1", title: "Spend", key: "cost", visible: true, category: "Performance", isLocked: false },
    { id: "3", title: "Remaining Budget", key: "Remaining Budget", visible: true, category: "Performance", isLocked: false },
    { id: "4", title: "Avg. Cost", key: "avg_cost", visible: false, category: "Performance", isLocked: false },
    { id: "5", title: "Avg. CPC", key: "avg_cpc", visible: false, category: "Performance", isLocked: false },
    { id: "6", title: "Avg. CPM", key: "avg_cpm", visible: false, category: "Performance", isLocked: false },
    { id: "7", title: "Budget", key: "budget_micros", visible: false, category: "Performance", isLocked: false },
    { id: "8", title: "Conversion", key: "conversion", visible: false, category: "Conversion", isLocked: false },
    { id: "9", title: "Cost / Conv", key: "cost_per_conversion", visible: false, category: "Conversion", isLocked: false },
    { id: "10", title: "Impr.", key: "impressions", visible: true, category: "Performance", isLocked: false },
    { id: "11", title: "Leads", key: "leads", visible: true, category: "Performance", isLocked: false },
    { id: "12", title: "Interaction Rate", key: "interaction_rate", visible: false, category: "Performance", isLocked: false },
    { id: "13", title: "Clicks", key: "clicks", visible: false, category: "Performance", isLocked: false },
    { id: "14", title: "CTR", key: "ctr", visible: true, category: "Performance", isLocked: false },
    { id: "15", title: "Spend to Date", key: "spend_to_date", visible: true, category: "Performance", isLocked: false },
    { id: "16", title: "Yesterday Spend", key: "yesterday_spend", visible: true, category: "Performance", isLocked: false },
  ]);

  const uniqueCategories = Array.from(
    new Set(columns.map((col) => col.category)),
  );

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() +1, 0);

  const [state, setState] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonth,
      key: "selection",
    },
  ]);

  const [appliedState, setAppliedState] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: lastDayOfMonth,
      key: "selection",
    },
  ]);

  const getDateParams = (currentDateState) => {
    const targetState = currentDateState || appliedState;
    const startDate = format(targetState[0].startDate, "yyyy-MM-dd");
    const endDate = format(targetState[0].endDate, "yyyy-MM-dd");

    let prevDates = {};
    if (targetState.length > 1) {
      const PrevstartDate = format(targetState[1].startDate, "yyyy-MM-dd");
      const PrevendDate = format(targetState[1].endDate, "yyyy-MM-dd");
      prevDates = {
        previous_start_date: PrevstartDate,
        previous_end_date: PrevendDate,
      };
    }
    return { startDate, endDate, prevDates };
  };

  const processCardData = (data) => {
    let existingKeys = cards.map((item) => item.key);
    let customKeys = Object.keys(data).filter(
      (key) =>
        !existingKeys.includes(key) &&
        !key.includes("interaction") &&
        !key.includes("currency") &&
        !key.includes("_previous_percentage"),
    );

    if (customKeys.length > 0) {
      let appendedCols = customKeys.map((key) => ({
        id: key,
        title: key,
        key: key,
        category: "Custom Columns",
        color: "text-[#4CAF50]",
        visible: false,
      }));

      setCards((prev) => {
        const newCols = appendedCols.filter(
          (ac) => !prev.some((p) => p.key === ac.key),
        );
        return [...prev, ...newCols];
      });
    }
  };

  const fetchDashboardMetrics = async (currentDateState) => {
    setIsLoading(true);
    const { startDate, endDate, prevDates } = getDateParams(currentDateState);

    const requestBody = apiRequestBodyBuilder(null, {
      start_date: startDate,
      end_date: endDate,
      ...prevDates,
    });

    try {
      const res = await axios.post(
        "https://api.confidanto.com/campaigns/overall-metrics",
        requestBody,
      );
      const data = Array.isArray(res.data) ? res.data[0] : res.data;

      processCardData(data);
      setMetricsData(data);
    } catch (err) {
      console.error("Error fetching dashboard metrics", err);
    } finally {
      setIsLoading(false);
    }
  };

  // API Call to fetch Budget Alert status
  const checkBudgetAlert = async () => {
    try {
      const requestBody = {
        project_id: parseInt(projectId, 10),
        customer_id: localStorage.getItem("customer_id"),
        email: localStorage.getItem("email"),
      };
      
      const res = await axios.post(
        "https://api.confidanto.com/monthly-budget/check-budget-alert",
        requestBody
      );

      if (res.data?.message?.includes("No budget")) {
        setBudgetAlert({ show: true, exists: false, message: "No Budget" });
      } else {
        setBudgetAlert({ show: true, exists: true, message: "Budget Active" });
      }
    } catch (err) {
      console.error("Error fetching budget alert", err);
    }
  };

  useEffect(() => {
    const fetchCustomColumns = async () => {
      try {
        const requestBody = apiRequestBodyBuilder(null);
        axios.post("https://api.confidanto.com/custom_columns/get-custom-columns", requestBody);
      } catch (err) {
        console.error("Error fetching custom columns", err);
      }
    };
    const fetchFunc = async () => {
      checkBudgetAlert(); // Call the budget alert check concurrently
      await fetchCustomColumns();
      await fetchDashboardMetrics();
      const updatedCards = await getPriorityColumnsFromStorage(cards);
      setCards(updatedCards);
    };
    if (projectId) {
      fetchFunc();
    }
  }, [projectId]);

  const handleApplyDateRange = () => {
    setAppliedState(state);
    setShowDatePicker(false);
    fetchDashboardMetrics(state);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) setShowDatePicker(false);
      if (cardColumnsRef.current && !cardColumnsRef.current.contains(event.target)) setShowCardColumns(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getColor = (value, key) => {
    const lowerIsBetter = ["costs", "average_cost", "average_cpc", "average_cpm", "cost_per_conversion"].includes(key);
    if (value > 0) return lowerIsBetter ? "bg-red-50 text-red-700 border border-red-100" : "bg-green-50 text-green-700 border border-green-100";
    if (value < 0) return lowerIsBetter ? "bg-green-50 text-green-700 border border-green-100" : "bg-red-50 text-red-700 border border-red-100";
    return "bg-gray-50 text-gray-600 border border-gray-200";
  };

  const getIconComponent = (value) => {
    if (value > 0) return <FaArrowUp className="inline-block mr-1" />;
    if (value < 0) return <FaArrowDown className="inline-block mr-1" />;
    return <FaArrowRight className="inline-block mr-1" />;
  };

  const formatButtonLabel = () => {
    const { startDate, endDate } = appliedState[0];
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      return `${format(startDate, "MMM d")} - ${format(endDate, "d, yyyy")}`;
    } else {
      return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
    }
  };

  function compareDateRanges() {
    if (state.length > 1) {
      setState((prevState) => [prevState[0]]);
    } else {
      let d = new Date();
      let month = d.getMonth();
      let year = d.getFullYear();
      let newCompareRange = {
        startDate: new Date(year, month - 1, 1),
        endDate: new Date(year, month, 0),
        key: "compare",
      };
      setState((prevState) => [...prevState, newCompareRange]);
    }
  }

  function handleSelectDateRanges(ranges) {
    const { selection, compare } = ranges;
    if (selection) setState((prev) => { const newState = [...prev]; newState[0] = selection; return newState; });
    else if (compare) setState((prev) => { const newState = [...prev]; if (newState.length > 1) newState[1] = compare; return newState; });
  }

  const navigateProjectList = () => {
    localStorage.removeItem("project_name");
    localStorage.removeItem("project_id");
    localStorage.removeItem("customer_id");
    navigate(`/projects`);
  };

  const handleDownloadPdf = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Project_Details_Report_${format(new Date(), "yyyy-MM-dd")}`,
    pageStyle: `
      @media print {
        body { -webkit-print-color-adjust: exact; }
        @page { size: landscape; margin: 8mm; }
        html { zoom: 0.9; }
      }
    `
  });

  const handleDownloadDoc = async () => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        windowWidth: reportRef.current.scrollWidth,
        windowHeight: reportRef.current.scrollHeight,
        scrollY: -window.scrollY 
      });
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pageHeightInPixels = Math.floor(imgWidth * 1.41);
      let heightLeft = imgHeight;
      let position = 0;
      const imageSlices = [];

      while (heightLeft > 0) {
        const currentSliceHeight = Math.min(heightLeft, pageHeightInPixels);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = currentSliceHeight;
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0,
          position,
          imgWidth,
          currentSliceHeight,
          0,
          0,
          imgWidth,
          currentSliceHeight,
        );

        imageSlices.push({ src: pageCanvas.toDataURL("image/png"), height: currentSliceHeight });
        heightLeft -= pageHeightInPixels;
        position += pageHeightInPixels;
      }

      const docWidth = 640;
      const imgsHtml = imageSlices
        .map((slice) => {
          const docHeight = Math.floor((slice.height / imgWidth) * docWidth);
          return `<img src="${slice.src}" width="${docWidth}" height="${docHeight}" style="display:block; margin: 0 auto;" />`;
        })
        .join("<br style='page-break-after: always; clear: both;' />");

      const htmlContent = `
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>Report</title>
          <style>
            @page { size: 8.5in 11in; margin: 1in; }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body><div class="Section1">${imgsHtml}</div></body>
        </html>
      `;
      const blob = new Blob(["\ufeff", htmlContent], { type: "application/msword" });
      FileSaver.saveAs(blob, "project_details_report.doc");
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDownload = (format) => {
    setShowDownloadOptions(false);
    if (format === "PDF") handleDownloadPdf();
    else if (format === "Google doc") handleDownloadDoc();
  };

  const platforms = JSON.parse(localStorage.getItem("platform_connected") || "{}");
  const showGoogleAdsIcon = !!platforms?.google_ads;
  const showMicrosoftAdsIcon = !!platforms?.microsoft_ads;
  const [showYOY, setShowYOY] = useState(true);

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-gray-50 to-gray-100  text-gray-900">
      <div className="w-full max-w-[1800px] mx-auto shadow-sm bg-white min-h-screen flex flex-col">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center justify-between p-2 md:p-6 gap-4 border-b border-gray-200 bg-white sticky top-0 z-30">
          <h1 className="text-xl sm:text-xl md:text-2xl flex items-center font-extrabold flex-1 min-w-0">
            <button
              onClick={navigateProjectList}
              className="mr-2 sm:mr-4 p-1.5 sm:p-2 transition-colors hover:bg-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 shrink-0"
            >
              <IoIosArrowBack className="h-6 w-6 sm:h-8 sm:w-8" />
            </button>
            <span className="truncate">Project Details</span>
          </h1>
          <div className="relative w-full sm:w-auto flex justify-start sm:justify-end shrink-0">
            <button
              className={`flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm sm:text-base font-medium bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${isLoading ? "pointer-events-none opacity-50" : ""}`}
              onClick={() => setShowDownloadOptions(!showDownloadOptions)}
            >
               <MdOutlineFileDownload className="text-xl shrink-0" />
               <span className="truncate">Download</span>
            </button>
            {showDownloadOptions && (
              <div className="absolute right-0 sm:right-auto sm:left-0 top-full bg-white shadow-xl rounded-lg p-2 mt-2 z-50 border border-gray-200 w-48 animate-fade-in">
                {["PDF", "Google doc"].map((fmt) => (
                  <button key={fmt} onClick={() => handleDownload(fmt)} className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-md transition-colors">
                    {fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 p-4 sm:p-6 md:p-8 overflow-x-hidden">
          <div className="relative w-full print:m-0 print:p-4" ref={reportRef}>
            {/* Control Row with Calendar, Alert, and Toggles */}
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center mb-6 gap-4 relative z-20 print:hidden">
              {/* Left Side: Calendar & Budget Alert */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-auto" ref={datePickerRef}>
                  <button
                    onClick={() => {
                      if (!showDatePicker) setState(appliedState);
                      setShowDatePicker(!showDatePicker);
                    }}
                    className="w-full sm:w-auto flex justify-between items-center py-2.5 px-4 sm:px-5 bg-white text-gray-800 rounded-lg font-medium shadow-sm border border-gray-300 hover:bg-gray-50 transition-colors text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span className="truncate">{formatButtonLabel()}</span>
                  </button>
                  {showDatePicker && (
                    <div className="absolute z-50 mt-2 shadow-2xl bg-white left-0 rounded-xl border border-gray-200 overflow-hidden w-[calc(100vw-2rem)] sm:w-auto max-w-[100vw] overflow-x-auto">
                      <DateRangePicker onChange={handleSelectDateRanges} showSelectionPreview={true} moveRangeOnFirstSelection={false} months={1} ranges={state} maxDate={new Date()} direction="horizontal" />
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-gray-50 border-t border-gray-200">
                        <button onClick={handleApplyDateRange} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Apply</button>
                        <Switcher7 onToggle={compareDateRanges} flag={state.length > 1} />
                      </div>
                    </div>
                  )}
                </div>
                {/* Budget Alert Badge */}
                {budgetAlert.show && (
                  <div 
                    title={budgetAlert.exists ? "Budget is configured for this month" : "No budget configured for this month"}
                    className={`flex items-center justify-center px-4 py-2.5 rounded-lg border text-sm font-semibold shadow-sm w-full sm:w-auto transition-colors ${
                      budgetAlert.exists 
                        ? "bg-green-50 text-green-700 border-green-200" 
                        : "bg-orange-50 text-orange-700 border-orange-200"
                    }`}
                  >
                    {budgetAlert.exists ? (
                      <FaCheckCircle className="mr-2 text-green-600 shrink-0 text-base" />
                    ) : (
                      <FaExclamationCircle className="mr-2 text-orange-600 shrink-0 text-base" />
                    )}
                    <span className="truncate">{budgetAlert.message}</span>
                  </div>
                )}
              </div>
              {/* Right Side: YOY Toggle & Cards Config */}
              <div className="relative flex justify-end w-full md:w-auto space-x-4" ref={cardColumnsRef}>
                {appliedState.length > 1 && (
                  <div className="flex items-center justify-between sm:justify-center gap-3 bg-white border border-gray-300 px-4 py-2.5 rounded-lg shadow-sm w-full sm:w-auto transition-colors hover:bg-gray-50">
                    <span className="text-sm font-medium text-gray-700">Show YOY</span>
                    <Switcher7 onToggle={() => setShowYOY(!showYOY)} flag={showYOY} hideCompareText={true} />
                  </div>
                )}
                <button className="flex items-center justify-center bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg shadow-sm hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-blue-500 w-full sm:w-auto" onClick={() => setShowCardColumns(!showCardColumns)}>
                  <PiCardsBold className="text-xl sm:text-2xl" />
                </button>
                {showCardColumns && (
                  <div className="absolute right-0 top-full mt-2 z-30 bg-white shadow-2xl rounded-xl border border-gray-200 w-[calc(100vw-2rem)] sm:w-72 md:w-80 p-3 max-w-sm">
                    <ModifyCardsDrag cards={cards} setCards={setCards} setTableVisible={setTableVisible} setShowCardColumns={setShowCardColumns} />
                  </div>
                )}
              </div>
            </div>
            <div className="print:block print:break-inside-avoid print:page-break-inside-avoid">
              <div className="mb-8 print:mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6 print:gap-2 relative z-10 print:block">
                {isLoading ? (
                  cards.filter((col) => col.visible).map((col, index) => (
                    <div key={`skeleton-${index}`} className="flex flex-col justify-between p-5 bg-white rounded-2xl shadow-sm border border-gray-100 min-h-[140px] animate-pulse print:hidden">
                      <div className="flex items-center space-x-3 w-full"><div className="h-8 w-8 bg-gray-200 rounded-full shrink-0"></div><div className="h-5 w-2/3 bg-gray-200 rounded"></div></div>
                      <div className="mt-6 h-8 w-1/2 bg-gray-200 rounded"></div><div className="self-end mt-2 h-5 w-16 bg-gray-200 rounded-full"></div>
                    </div>
                  ))
                ) : cards.filter((col) => col.visible).map((col) => {
                    const value = metricsData[col.key] || 0;
                    const changeKey = col.key + "_previous_percentage";
                    const YOYKey = col.key + "_yoy_percentage";
                    const diff = metricsData[changeKey];
                    const YOYvalue = metricsData[YOYKey];
                    return (
                      <div key={col.id || col.key} className="group relative flex flex-col justify-between p-5 print:p-3 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 min-h-[140px] print:min-h-0 print:mb-4 overflow-hidden print:inline-block print:w-[32%] print:align-top print:mr-[1%]">
                        <div className="flex items-center justify-start space-x-3 mb-4 print:mb-2">
                          <div className="flex flex-row shrink-0">
                            {showGoogleAdsIcon && showMicrosoftAdsIcon ? (
                              <div className="flex flex-row -space-x-2">
                                <div className="bg-white border border-gray-200 rounded-full p-1.5 z-10 shadow-sm"><img src={bingads} alt="Bing" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" /></div>
                                <div className="bg-white border border-gray-200 rounded-full p-1.5 z-20 shadow-sm"><img src={googleads} alt="Google" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" /></div>
                              </div>
                            ) : (
                              <>
                                {showGoogleAdsIcon && <div className="bg-white border border-gray-200 rounded-full p-1.5 shadow-sm"><img src={googleads} alt="Google" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" /></div>}
                                {showMicrosoftAdsIcon && <div className="bg-white border border-gray-200 rounded-full p-1.5 shadow-sm"><img src={bingads} alt="Bing" className="h-4 w-4 sm:h-5 sm:w-5 object-contain" /></div>}
                              </>
                            )}
                          </div>
                          <h3 className="text-gray-500 font-medium text-sm sm:text-base leading-tight truncate">{col.title}</h3>
                        </div>
                        <div className="flex items-end justify-between mt-auto gap-2">
                          <div className={`font-bold text-2xl sm:text-3xl print:text-xl truncate ${col.color}`}>
                            {(() => {
                              const wholeValues = ["clicks", "impressions", "conversions"];
                              const percentFields = ["ctr", "interaction_rate", "conversion_rate"];
                              const rupeeFields = ["average_cost", "costs", "remaining_budget", "average_cpc", "cost_per_conversion", "average_cpm"];
                              if (wholeValues.includes(col.key)) return Number(value).toLocaleString(getLocalCurrencyStringType(), { maximumFractionDigits: 0 });
                              if (percentFields.includes(col.key)) return value + "%";
                              if (rupeeFields.includes(col.key)) return currencySymbol + Number(value).toLocaleString(getLocalCurrencyStringType());
                              return Number(value).toLocaleString(getLocalCurrencyStringType());
                            })()}
                          </div>
                          {diff !== null && diff !== undefined && (
                            <div className="flex flex-col items-end space-y-1.5 shrink-0">
                              <div className={`flex items-center text-sm font-semibold px-2 py-0.5 rounded-md shadow-sm shrink-0 ${getColor(diff, col.key)}`}><span className="mr-0.5">{getIconComponent(diff)}</span>{Math.abs(diff || 0).toFixed(2)}%</div>
                              {showYOY && appliedState.length > 1 && (
                                <div className={`flex items-center text-sm font-semibold px-2 py-0.5 rounded-md shadow-sm shrink-0 bg-green-50 text-green-700 border border-green-100`}><span className="mr-0.5">{getIconComponent(YOYvalue)}</span>{Math.abs(YOYvalue || 0).toFixed(2)}% YOY</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div className="w-full bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200 overflow-x-auto print:overflow-visible print:border-none print:shadow-none print:p-0 print:block">
                <div className="min-w-[600px] print:min-w-0 print:w-full print:block">
                  <Bar startDate={format(appliedState[0].startDate, "yyyy-MM-dd")} endDate={format(appliedState[0].endDate, "yyyy-MM-dd")} customerId={localStorage.getItem("customer_id")} state={appliedState} />
                </div>
              </div>
            </div>
            <div className="flex flex-col space-y-6 sm:space-y-8 w-full z-0 relative mt-6 sm:mt-8 print:block print:space-y-0 print:mt-4">
              <div className="w-full bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200 overflow-x-auto print:overflow-visible print:border-none print:shadow-none print:p-0 print:block print:mb-4">
                <div className="min-w-[800px] print:min-w-0 print:w-full print:block">
                  <AddTable
                    startDate={format(appliedState[0].startDate, "yyyy-MM-dd")}
                    endDate={format(appliedState[0].endDate, "yyyy-MM-dd")}
                    email={localStorage.getItem("email")}
                    uniqueCategories={uniqueCategories}
                    customerId={localStorage.getItem("customer_id")}
                    initialDateRange={appliedState}
                    CampaignTable={CampaignTable}
                    setCardTotalFunction={(data) => {
                      processCardData(data);
                      setMetricsData(data);
                    }}
                    PageType={"project_details"}
                  />
                </div>
              </div>
              <div className="w-full bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200 overflow-x-auto print:overflow-visible print:border-none print:shadow-none print:p-0 print:block">
                <div className="min-w-[600px] print:min-w-0 print:w-full print:block">
                  <AddChart
                    PageType={"project_details"}
                    previousDates={{
                      curr_start_date: format(appliedState[0].startDate, "yyyy-MM-dd"),
                      curr_end_date: format(appliedState[0].endDate, "yyyy-MM-dd"),
                      prev_start_date: appliedState[1] ? format(appliedState[1]?.startDate, "yyyy-MM-dd") : null,
                      prev_end_date: appliedState[1] ? format(appliedState[1]?.endDate, "yyyy-MM-dd") : null,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;